import React, { useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { ScoreResultPayload } from './scoreResultUrl';
import { mailtoAuditFallback, submitLead } from '../lib/leads';
import { trackAuditRequest } from '../lib/analytics';
type AuditRequestFormProps = {
  score?: ScoreResultPayload | null;
  helperText: string;
};
export function AuditRequestForm({
  score = null,
  helperText
}: AuditRequestFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'mailto' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const hasScore = Boolean(score);
  const mailtoContext = () => ({
    name: name.trim() || undefined,
    email: email.trim() || undefined,
    note: note.trim() || undefined,
    pageUrl: typeof window !== 'undefined' ? window.location.href : undefined
  });
  const openMailtoOnce = () => {
    const href = mailtoAuditFallback(score, mailtoContext());
    // Assign once — avoids duplicate windows from rapid clicks / popup + assign.
    window.location.assign(href);
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || status === 'submitting' || status === 'success') {
      return;
    }
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      setStatus('error');
      return;
    }
    submittingRef.current = true;
    setStatus('submitting');
    setError(null);
    const result = await submitLead({
      name: name.trim(),
      email: email.trim(),
      note: note.trim() || undefined,
      source: 'audit_request',
      score
    });
    if (result.ok) {
      trackAuditRequest('form');
      setStatus('success');
      submittingRef.current = false;
      return;
    }

    // Fall back to mailto when plumbing is missing or failed.
    trackAuditRequest('mailto_fallback');
    setStatus('mailto');
    setError(result.mode === 'error' ? result.error || 'Could not save your request online. Your email app will open instead.' : 'Your email app will open with your score details so you can send the request.');
    openMailtoOnce();
    // Keep mailto state so a second click does not spawn another compose window.
    submittingRef.current = false;
  };
  if (status === 'success') {
    return <div className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" data-id="element-35" data-mp="2hya08034ij9p">
        <h2 className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-36" data-mp="2czf6w47j5cbc">
          Request received.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-37" data-mp="2mzpixlhtwdox">
          Thanks{name ? `, ${name.split(' ')[0]}` : ''}. We’ll follow up about a
          Reader Path Audit
          {hasScore ? ' using the score you just completed' : ''}.
        </p>
      </div>;
  }
  return <section className="mt-10 rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" aria-labelledby="audit-heading" data-id="element-38" data-mp="1u8xpgvs5e35a">
      <h2 id="audit-heading" className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-39" data-mp="ejg231k58b3j">
        See what a clearer path could unlock.
      </h2>
      <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-40" data-mp="3w1sipsqvll35">
        {helperText}
      </p>

      <form onSubmit={onSubmit} className="mx-auto mt-8 max-w-md space-y-3 text-left" data-id="element-41" data-mp="4ug2bfxthboa">
        <label className="block" data-id="element-42" data-mp="sksdczoczoj3">
          <span className="sr-only" data-id="element-43" data-mp="sho61tflkco7">Name</span>
          <input type="text" name="name" autoComplete="name" required placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-44" data-mp="xhiulqbwgq63" />
        </label>
        <label className="block" data-id="element-45" data-mp="347qet7cmm4hi">
          <span className="sr-only" data-id="element-46" data-mp="38an3nfsrewtw">Email</span>
          <input type="email" name="email" autoComplete="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-47" data-mp="38bemrr56723a" />
        </label>
        <label className="block" data-id="element-48" data-mp="3epvoxc5air2l">
          <span className="sr-only" data-id="element-49" data-mp="39tdkkddvguqs">Optional note</span>
          <textarea name="note" rows={3} placeholder="Optional note (timeline, book title, etc.)" value={note} onChange={e => setNote(e.target.value)} className="w-full resize-y rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-50" data-mp="39uol84c0tmdo" />
        </label>

        {error && <p className="text-sm text-sand" role="alert" data-id="element-51" data-mp="3thmd4ojbv2qt">
            {error}
          </p>}

        <button type="submit" disabled={status === 'submitting' || status === 'mailto'} className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-sand px-8 py-4 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy disabled:cursor-not-allowed disabled:opacity-60" data-id="element-52" data-mp="2m3sqayc9qb27">
          {status === 'submitting' ? 'Sending…' : status === 'mailto' ? 'Email app opening…' : 'Request a Reader Path Audit'}
          <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" data-id="element-53" data-mp="1bcctsb7xlmmh" />
        </button>
      </form>

      <p className="mt-4 text-sm text-paper/55" data-id="element-54" data-mp="1g9muwo4onmq5">
        We’ll follow up by email
        {hasScore ? '. Your score details are included automatically.' : '.'}
      </p>

      {status !== 'mailto' ? <a href={mailtoAuditFallback(score, mailtoContext())} onClick={() => trackAuditRequest('mailto_fallback')} className="mt-3 inline-block text-sm font-medium text-seaglass-light underline underline-offset-4 hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-55" data-mp="1pbcghdbvvrg0">
          Prefer email instead
        </a> : <p className="mt-3 text-sm text-paper/70" data-id="element-56" data-mp="3w33sni7mp591">
          If your email app did not open, use Prefer email again after refreshing,
          or write john@growththatholds.com.
        </p>}
    </section>;
}